import { JourneyNode, JourneyNodeDifficulty, JourneyNodeType } from './journeyPathConfig';

// XP reward values per node type and difficulty
const PROJECT_XP: Record<string, number> = {
  easy: 50,
  medium: 100,
  hard: 200,
};

const PROBLEM_XP: Record<string, number> = {
  easy: 30,
  medium: 60,
  hard: 120,
};

const BASE_XP: Record<JourneyNodeType, number> = {
  project: 50,
  challenge: 75,
  problem: 30,
  'code-frame': 50,
  prototype: 100,
  cv: 150,
};

// Only these types actually grant XP in the backend
const XP_GRANTED_TYPES = new Set<JourneyNodeType>(['project', 'challenge', 'problem']);

/**
 * Whether this node type grants XP when completed.
 * Code frames, prototypes, and CV do not grant experience points.
 */
export const nodeGrantsXp = (type: JourneyNodeType): boolean =>
  XP_GRANTED_TYPES.has(type);

/**
 * Get XP reward for a journey node based on type and difficulty.
 * Returns 0 for types that don't grant XP.
 */
export const getNodeXpReward = (
  type: JourneyNodeType,
  difficulty?: JourneyNodeDifficulty
): number => {
  if (!nodeGrantsXp(type)) return 0;
  if (type === 'project' && difficulty) {
    return PROJECT_XP[difficulty] ?? BASE_XP.project;
  }
  if (type === 'problem' && difficulty) {
    return PROBLEM_XP[difficulty] ?? BASE_XP.problem;
  }
  return BASE_XP[type] ?? 50;
};

// Type-specific accent colors
const NODE_TYPE_COLORS: Record<JourneyNodeType, string> = {
  project: 'purple.400',
  challenge: 'orange.400',
  problem: 'green.400',
  'code-frame': 'blue.400',
  prototype: 'pink.400',
  cv: 'yellow.400',
};

/**
 * Get the accent color for a journey node type.
 */
export const getNodeTypeColor = (type: JourneyNodeType): string =>
  NODE_TYPE_COLORS[type] ?? 'purple.400';

// Raw hex colors for CSS contexts where Chakra tokens don't work
const NODE_TYPE_RAW_COLORS: Record<JourneyNodeType, string> = {
  project: '#B794F4',
  challenge: '#ED8936',
  problem: '#68D391',
  'code-frame': '#63B3ED',
  prototype: '#F687B3',
  cv: '#F6E05E',
};

export const getNodeTypeRawColor = (type: JourneyNodeType): string =>
  NODE_TYPE_RAW_COLORS[type] ?? '#B794F4';

// Human-friendly type labels
const NODE_TYPE_LABELS: Record<JourneyNodeType, string> = {
  project: 'Project',
  challenge: 'Challenge',
  problem: 'Problem',
  'code-frame': 'Code Frame',
  prototype: 'Prototype',
  cv: 'CV Builder',
};

export const getNodeTypeLabel = (type: JourneyNodeType): string =>
  NODE_TYPE_LABELS[type] ?? 'Unknown';

// Difficulty display info
const DIFFICULTY_CONFIG: Record<string, { label: string; color: string }> = {
  easy: { label: 'Easy', color: '#68D391' },
  medium: { label: 'Medium', color: '#ED8936' },
  hard: { label: 'Hard', color: '#FC8181' },
  expert: { label: 'Expert', color: '#F687B3' },
};

export const getDifficultyConfig = (
  difficulty?: JourneyNodeDifficulty
): { label: string; color: string } | null => {
  if (!difficulty) return null;
  return DIFFICULTY_CONFIG[difficulty] ?? null;
};

/**
 * Calculate the current consecutive-day activity streak ending today.
 * Returns 0 if there's no activity today.
 */
export const calculateCurrentStreak = (activityDates: string[]): number => {
  if (activityDates.length === 0) return 0;

  // Collect unique YYYY-MM-DD dates
  const uniqueDays = new Set<string>();
  for (const dateStr of activityDates) {
    uniqueDays.add(dateStr.slice(0, 10));
  }

  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);

  if (!uniqueDays.has(todayKey)) return 0;

  let streak = 1;
  const cursor = new Date(today);

  while (true) {
    cursor.setDate(cursor.getDate() - 1);
    const key = cursor.toISOString().slice(0, 10);
    if (uniqueDays.has(key)) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
};

/**
 * Count total unique active days from activity date strings.
 */
export const calculateTotalActiveDays = (activityDates: string[]): number => {
  if (activityDates.length === 0) return 0;

  const uniqueDays = new Set<string>();
  for (const dateStr of activityDates) {
    uniqueDays.add(dateStr.slice(0, 10));
  }
  return uniqueDays.size;
};

/** Number of weeks to show in the heatmap — ~5 months, fits within the 380px card without overflow. */
export const HEATMAP_WEEKS = 20;

/** How many days to show — 13 full weeks (91 days). */
export const getDaysToShow = (): number => HEATMAP_WEEKS * 7;

/** Calculate the start date for activity queries — 91 days ago. */
export const getActivityStartDate = (): string => {
  const start = new Date(Date.now() - getDaysToShow() * 24 * 60 * 60 * 1000);
  return start.toISOString().slice(0, 10);
};

// ─── Node Description & CTA ─────────────────────────────────────────────────

const CODE_FRAME_DESCRIPTIONS: Record<string, string> = {
  'Component Library': 'Build a reusable UI component library with buttons, inputs, and cards.',
  'Dashboard Widget': 'Create a self-contained dashboard widget with live data display.',
  'Interactive Form': 'Build a dynamic form with validation, conditional fields, and submission.',
  'Design System': 'Create a design system with tokens, components, and documentation.',
  'State Manager': 'Build a lightweight state management solution from scratch.',
  'API Client Library': 'Create a typed API client with error handling and request caching.',
  'Open Source Component': 'Build a polished, reusable component ready for open source.',
  'Animated Button': 'Create a button with creative hover effects and micro-animations.',
  'Social Card': 'Build a social media card component with avatar, stats, and actions.',
  'Data Visualization': 'Create an interactive data visualization with charts or graphs.',
  'Chart Component': 'Build a reusable chart component with configurable data and styles.',
  'Rich Text Editor': 'Create a rich text editor with formatting, lists, and embeds.',
  'Animation Library': 'Build a collection of reusable CSS and JS animation utilities.',
  'Interactive Component': 'Build an interactive UI component with state and user input.',
};

const PROTOTYPE_DESCRIPTIONS: Record<string, string> = {
  'Your Portfolio': 'Design and build your personal developer portfolio from scratch.',
  'SaaS Landing Page': 'Create a conversion-focused landing page for a SaaS product.',
  'MVP Dashboard': 'Prototype a minimum viable product dashboard with key metrics.',
  'Production App': 'Build a production-ready web application with real functionality.',
  'Real-Time Dashboard': 'Create a live dashboard with real-time data updates and alerts.',
  'Full-Stack SaaS': 'Prototype a complete SaaS application with frontend and backend.',
  'Personal Portfolio': 'Build a standout portfolio site that showcases your best work.',
  'Fun Side Project': 'Create something fun and creative — no rules, just build.',
  'Interactive Portfolio': 'Build a portfolio with animations, transitions, and interactivity.',
  'Creative Showcase': 'Create a visually stunning showcase of your creative work.',
  'Design System': 'Build a comprehensive design system with tokens and components.',
  'Open Source Tool': 'Prototype a developer tool ready to share with the community.',
  'Creative Project': 'Build something creative and unique to showcase your skills.',
  'Explore Flights': 'Create a flight search and exploration interface with filters.',
};

const PROJECT_DESCRIPTIONS: Record<string, string> = {
  'todo-app': 'Build a classic task manager with add, complete, and delete functionality.',
  'music-player': 'Create an interactive music player with playback controls and playlists.',
  'tic-tac-toe': 'Build the classic two-player game with win detection and game state.',
  'business-card': 'Design a personal business card with contact info and social links.',
  'character-generator': 'Create a fun character generator with randomized attributes and visuals.',
  'ai-post-generator': 'Build an AI-powered tool that generates social media posts.',
  'color-memory': 'Create a memory card game using colors and pattern matching.',
  'the-veggie-king': 'Build a veggie-themed ordering app with cart and checkout flow.',
  'pokedex': 'Create a searchable Pokedex using the PokeAPI with detailed Pokemon info.',
  'code-editor': 'Build a browser-based code editor with syntax highlighting and preview.',
  'quiz-app': 'Create an interactive quiz app with scoring, timers, and results.',
  'blog': 'Build a full-featured blog with posts, categories, and rich text editing.',
  'dashboard': 'Create a data dashboard with charts, metrics, and interactive widgets.',
  'just-weather': 'Build a weather app that fetches real-time forecasts by location.',
  'level-editor': 'Create a drag-and-drop level editor for building game environments.',
  'calculator': 'Build a calculator with standard operations and a polished interface.',
  'notely': 'Create a note-taking app with rich text, tags, and search.',
  'giftok': 'Build a GIF-browsing app with search, favorites, and sharing.',
  'find-movies': 'Create a movie discovery app with search, filters, and detailed views.',
  'foodiego': 'Build a food delivery app with restaurant listings and order flow.',
  'any-chat': 'Create a real-time chat application with rooms and messaging.',
};

/**
 * Get a short description for a journey node based on its type and screenName.
 */
export const getNodeDescription = (node: JourneyNode): string => {
  if (node.type === 'project' && node.screenName && PROJECT_DESCRIPTIONS[node.screenName]) {
    return PROJECT_DESCRIPTIONS[node.screenName];
  }

  switch (node.type) {
    case 'project':
      return `Build a ${node.label} from scratch.`;
    case 'challenge':
      return 'A daily UI challenge to sharpen your frontend skills.';
    case 'problem':
      return 'Solve this coding problem to strengthen your algorithmic thinking.';
    case 'code-frame':
      return CODE_FRAME_DESCRIPTIONS[node.label] || `Create a ${node.label} using our in-browser code editor.`;
    case 'prototype':
      return PROTOTYPE_DESCRIPTIONS[node.label] || `Prototype a ${node.label} to bring your ideas to life.`;
    case 'cv':
      return 'Craft a polished developer CV to land your dream role.';
    default:
      return 'Complete this step to continue your journey.';
  }
};

const NODE_CTA_LABELS: Record<JourneyNodeType, string> = {
  project: 'Start Project',
  challenge: 'Start Challenge',
  problem: 'Solve Problem',
  'code-frame': 'Open Editor',
  prototype: 'Create Prototype',
  cv: 'Build CV',
};

/**
 * Get the CTA button label for a journey node.
 */
export const getNodeCtaLabel = (node: JourneyNode): string =>
  NODE_CTA_LABELS[node.type] ?? 'Continue';

const NODE_OPEN_LABELS: Record<JourneyNodeType, string> = {
  project: 'Open Project',
  challenge: 'Open Challenge',
  problem: 'Open Problem',
  'code-frame': 'Open Editor',
  prototype: 'Open Prototype',
  cv: 'Open CV Builder',
};

/**
 * Get the "Open" button label for a journey node.
 */
export const getNodeOpenLabel = (node: JourneyNode): string =>
  NODE_OPEN_LABELS[node.type] ?? 'Open';

const NODE_COMPLETE_LABELS: Record<JourneyNodeType, string> = {
  project: 'Submit Solution',
  challenge: 'Submit Solution',
  problem: 'Submit Solution',
  'code-frame': 'Mark Complete',
  prototype: 'Mark Complete',
  cv: 'Mark Complete',
};

/**
 * Get the "Complete" button label for a journey node.
 */
export const getNodeCompleteLabel = (node: JourneyNode): string =>
  NODE_COMPLETE_LABELS[node.type] ?? 'Complete';

/**
 * Get the submission route for a journey node (project/challenge/problem only).
 * Returns the base entity route for types without submission pages.
 */
export const getNodeSubmitRoute = (node: JourneyNode): string | null => {
  if (node.screenName) {
    switch (node.type) {
      case 'project': return `/projects/${node.screenName}/submit`;
      case 'challenge': return `/challenges/${node.screenName}/submit`;
      case 'problem': return `/problems/${node.screenName}/submit`;
      default: return null;
    }
  }
  return null;
};

export type NodeState = 'completed' | 'current' | 'available';

/** Determine the state of a journey node based on its global index and completed count.
 *  When completedCount is 0, all nodes are 'available' (player hasn't started yet). */
export const getNodeState = (globalIndex: number, completedCount: number): NodeState => {
  if (completedCount === 0) return 'available';
  if (globalIndex < completedCount) return 'completed';
  if (globalIndex === completedCount) return 'current';
  return 'available';
};

export interface CompletionData {
  /** Completed project screenNames from DB */
  completedProjects: Set<string>;
  /** Completed challenge screenNames from DB */
  completedChallenges: Set<string>;
  /** Completed problem screenNames from DB */
  completedProblems: Set<string>;
  /** Manually completed node IDs from localStorage (cv, code-frame, prototype) */
  manualCompletions: Set<string>;
}

/**
 * Check if a journey node is completed.
 * - project/challenge/problem: matched by screenName from DB
 * - code-frame/prototype/cv: matched by node ID from localStorage manual completions
 */
const safeHas = (set: unknown, value: string): boolean =>
  set instanceof Set ? set.has(value) : false;

const isNodeCompleted = (
  node: JourneyNode,
  data: CompletionData
): boolean => {
  switch (node.type) {
    case 'project':
      return node.screenName ? safeHas(data.completedProjects, node.screenName) : false;
    case 'challenge':
      return node.screenName ? safeHas(data.completedChallenges, node.screenName) : false;
    case 'problem':
      return node.screenName ? safeHas(data.completedProblems, node.screenName) : false;
    case 'code-frame':
    case 'prototype':
    case 'cv':
      return safeHas(data.manualCompletions, node.id);
    default:
      return false;
  }
};

/**
 * Compute per-node completion state.
 * - project/challenge/problem: matched by screenName from DB
 * - code-frame/prototype/cv: matched by node ID from localStorage
 *
 * Returns a Map of nodeId → NodeState.
 * The first incomplete node is marked 'current', all after are 'available'.
 */
export const getNodeStatesMap = (
  allNodes: JourneyNode[],
  completions: CompletionData
): Map<string, NodeState> => {
  const states = new Map<string, NodeState>();
  let foundCurrent = false;

  for (const node of allNodes) {
    if (isNodeCompleted(node, completions)) {
      states.set(node.id, 'completed');
    } else if (!foundCurrent) {
      states.set(node.id, 'current');
      foundCurrent = true;
    } else {
      states.set(node.id, 'available');
    }
  }

  return states;
};

// ─── Manual Completion (localStorage) ──────────────────────────────────────
// CV, code-frame, and prototype nodes can't be auto-detected as completed,
// so we store manual completions in localStorage per user.

const MANUAL_COMPLETION_KEY_PREFIX = 'bds-journey-manual-';

/** Types that require manual completion tracking via localStorage. */
export const MANUAL_COMPLETION_TYPES = new Set<JourneyNodeType>([
  'cv',
  'code-frame',
  'prototype',
]);

const getManualCompletionKey = (userId: string): string =>
  `${MANUAL_COMPLETION_KEY_PREFIX}${userId}`;

/** Get all manually completed node IDs for a user from localStorage. */
export const getManualCompletions = (userId: string): Set<string> => {
  try {
    const raw = localStorage.getItem(getManualCompletionKey(userId));
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
};

/** Toggle manual completion for a node. Returns updated set. */
export const toggleManualCompletion = (userId: string, nodeId: string): Set<string> => {
  const completions = getManualCompletions(userId);
  if (completions.has(nodeId)) {
    completions.delete(nodeId);
  } else {
    completions.add(nodeId);
  }
  try {
    localStorage.setItem(getManualCompletionKey(userId), JSON.stringify(Array.from(completions)));
  } catch {
    // QuotaExceeded — silently fail
  }
  return completions;
};

// ─── Last Selection Persistence (localStorage) ─────────────────────────────
// Remembers which node the user last selected so it restores on page refresh.

const SELECTION_KEY_PREFIX = 'bds-journey-selection-';

const getSelectionKey = (userId: string): string =>
  `${SELECTION_KEY_PREFIX}${userId}`;

/** Get the last selected node ID and global index from localStorage. */
export const getLastSelection = (userId: string): { nodeId: string; globalIndex: number } | null => {
  try {
    const raw = localStorage.getItem(getSelectionKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.nodeId === 'string' && typeof parsed.globalIndex === 'number') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

/** Save the last selected node to localStorage. */
export const saveLastSelection = (userId: string, nodeId: string, globalIndex: number): void => {
  try {
    localStorage.setItem(getSelectionKey(userId), JSON.stringify({ nodeId, globalIndex }));
  } catch {
    // QuotaExceeded — silently fail
  }
};

/** Clear the last selection from localStorage. */
export const clearLastSelection = (userId: string): void => {
  try {
    localStorage.removeItem(getSelectionKey(userId));
  } catch {
    // silently fail
  }
};

/**
 * Count how many manually completed nodes match a given type in the journey path.
 * Walks the path and counts nodes whose IDs are in the manual completions set.
 */
export const countManualCompletionsByType = (
  manualCompletions: Set<string>,
  type: JourneyNodeType,
  nodes: JourneyNode[]
): number => {
  let count = 0;
  for (const node of nodes) {
    if (node.type === type && manualCompletions.has(node.id)) {
      count++;
    }
  }
  return count;
};
